import apiClient from "../../helpers/axios";

/*
 * Каталоги-журналы под печать (PDF).
 * BACKEND: controller/printCatalog.controller.js, маршруты в routes/catalogReview.js.
 * Каталог — это сохранённые настройки; PDF из них собирает сервер в фоне.
 */
const base = "/admin/catalog-review/print-catalogs";

export const DEFAULT_PRINT_OPTIONS = {
    perPage: 6,
    rowsPerPage: 3,
    tagMatch: "any",
    priceMode: "public",
    numbering: "sequence",
    sort: "name",
    showQr: true,
    showCover: true,
    showContacts: true,
    groupByCategory: true,
    onlyVisible: true,
    onlyWithPrice: false,
    onlyWithMilling: false,
    onlyTransferred: false,
    rangeFrom: 0,
    rangeTo: 0,
    imageCrop: "none",
    design: "classic",
    coverMode: "auto",
    coverArticle: "",
    markSource: "text",
    markText: "",
    photoMark: "none",
    pageMark: false,
    markStrength: "normal",
    showSign: true,
    signText: "",
    qrBaseUrl: "https://alotb.ru/productDetails/",
};

export const EMPTY_PRINT_DRAFT = {
    name: "",
    title: "Каталог памятников",
    subtitle: "Модели и цены",
    motto: "",
    companyName: "",
    edition: "",
    backText: "",
    contacts: { phone: "", site: "", address: "", hours: "" },
    url: "",
    accentColor: "#1f3b34",
    categoryIds: [],
    tags: [],
    options: DEFAULT_PRINT_OPTIONS,
};

const shapeCatalog = (row = {}) => ({
    ...row,
    _id: String(row?._id || ""),
    contacts: { phone: "", site: "", address: "", hours: "", ...(row?.contacts || {}) },
    categoryIds: Array.isArray(row?.categoryIds) ? row.categoryIds.map(String) : [],
    productIds: Array.isArray(row?.productIds) ? row.productIds.map(String) : [],
    tags: Array.isArray(row?.tags) ? row.tags.map(String).filter(Boolean) : [],
    options: { ...DEFAULT_PRINT_OPTIONS, ...(row?.options || {}) },
    build: {
        status: "idle",
        progress: 0,
        total: 0,
        message: "",
        startedAt: null,
        finishedAt: null,
        ...(row?.build || {}),
    },
    files: Array.isArray(row?.files) ? row.files : [],
    hasLogo: row?.hasLogo === true,
    hasCover: row?.hasCover === true,
});

/** Только то, что можно менять в каталоге, — это и уходит на сервер. */
export const toPrintDraft = (catalog) => ({
    name: catalog.name || "",
    title: catalog.title || "",
    subtitle: catalog.subtitle || "",
    motto: catalog.motto || "",
    companyName: catalog.companyName || "",
    edition: catalog.edition || "",
    backText: catalog.backText || "",
    contacts: { ...catalog.contacts },
    url: catalog.url || "",
    accentColor: catalog.accentColor || "#1f3b34",
    categoryIds: [...catalog.categoryIds],
    tags: [...catalog.tags],
    options: { ...catalog.options },
});

/**
 * Текст ошибки сервера. Когда ждали файл (PDF, картинку), ответ с ошибкой
 * тоже приходит «файлом» — его нужно сначала прочитать.
 */
export const readApiError = async (error, fallback) => {
    const data = error?.response?.data;

    if (typeof Blob !== "undefined" && data instanceof Blob) {
        try {
            const parsed = JSON.parse(await data.text());
            if (parsed?.message) return String(parsed.message);
        } catch (_error) {
            return fallback;
        }
    }

    return String(data?.message || fallback || error?.message || "Ошибка");
};

export const listPrintCatalogs = async () => {
    const response = await apiClient.get(base);
    return (Array.isArray(response.data?.catalogs) ? response.data.catalogs : []).map(shapeCatalog);
};

export const getPrintCatalog = async (catalogId) => {
    const response = await apiClient.get(`${base}/${catalogId}`);
    return shapeCatalog(response.data?.catalog);
};

export const createPrintCatalog = async (draft) => {
    const response = await apiClient.post(base, draft);
    return shapeCatalog(response.data?.catalog);
};

export const updatePrintCatalog = async (catalogId, draft) => {
    const response = await apiClient.patch(`${base}/${catalogId}`, draft);
    return shapeCatalog(response.data?.catalog);
};

export const deletePrintCatalog = async (catalogId) => {
    await apiClient.delete(`${base}/${catalogId}`);
    return true;
};

/** Запустить сборку PDF. Сервер отвечает сразу, сборка идёт в фоне. */
export const buildPrintCatalog = async (catalogId) => {
    const response = await apiClient.post(`${base}/${catalogId}/build`);
    return shapeCatalog(response.data?.catalog);
};

/** Логотип или фото обложки: картинка целиком в base64. kind: "logo" | "cover". */
export const uploadPrintCatalogImage = async (catalogId, kind, imageBase64) => {
    const response = await apiClient.post(`${base}/${catalogId}/${kind}`, { image: imageBase64 });
    return shapeCatalog(response.data?.catalog);
};

export const deletePrintCatalogImage = async (catalogId, kind) => {
    const response = await apiClient.delete(`${base}/${catalogId}/${kind}`);
    return shapeCatalog(response.data?.catalog);
};

/** Картинка логотипа или обложки. Адрес закрыт входом, поэтому забираем файлом. */
export const fetchPrintCatalogImage = async (catalogId, kind) => {
    const response = await apiClient.get(`${base}/${catalogId}/${kind}`, {
        responseType: "blob",
        params: { t: Date.now() },
    });
    return response.data;
};

/** Собранный PDF. */
export const fetchPrintCatalogFile = async (catalogId, fileId) => {
    const response = await apiClient.get(`${base}/${catalogId}/files/${fileId}`, { responseType: "blob" });
    return response.data;
};

const readNumberHeader = (headers, name) => {
    const value = Number(headers?.[name]);
    return Number.isFinite(value) ? value : 0;
};

/**
 * Пробник: обложка, две страницы товаров и контакты по настройкам с экрана
 * (можно несохранённым). В список PDF не попадает.
 * Возвращает файл и сколько товаров и страниц выйдет в полном каталоге.
 */
export const previewPrintCatalog = async ({ catalogId = "", draft }) => {
    const response = await apiClient.post(
        `${base}/preview`,
        { catalogId, catalog: draft },
        { responseType: "blob" }
    );

    let notes = "";
    try {
        notes = decodeURIComponent(response.headers?.["x-catalog-notes"] || "");
    } catch (_error) {
        notes = "";
    }

    return {
        blob: response.data,
        products: readNumberHeader(response.headers, "x-catalog-products"),
        pages: readNumberHeader(response.headers, "x-catalog-pages"),
        total: readNumberHeader(response.headers, "x-catalog-total"),
        notes,
    };
};

/** Категории товаров с числом товаров в каждой. */
export const getPrintCatalogCategories = async () => {
    const response = await apiClient.get("/admin/catalog-review/products/categories");
    return Array.isArray(response.data?.categories) ? response.data.categories : [];
};

/** Все теги, которые уже есть у товаров (самые частые — первыми). */
export const getPrintCatalogTags = async () => {
    const response = await apiClient.get("/admin/product-tags");
    return (Array.isArray(response.data?.tags) ? response.data.tags : [])
        .map((item) => ({ tag: String(item?.tag || "").trim(), count: Number(item?.count || 0) }))
        .filter((item) => item.tag);
};
